import { useState, useCallback, useRef, useEffect } from "react";
import PropTypes from "prop-types";

import BootstrapTable from "react-bootstrap-table-next";
import ToolkitProvider from "react-bootstrap-table2-toolkit";

import useSnackbar from "components/Alerts/Snackbar/useSnackbar";
import MaskAndValid from "utils/maskAndValid";

import {
  Box,
  CircularProgress,
  Grid,
  Typography,
} from "@material-ui/core";

import { makeStyles } from "@material-ui/core/styles";

import {
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Refresh as RefreshIcon,
  Cached as CacheIcon,
  Error as ErrorIcon,
} from "@material-ui/icons";

import componentStyles from "assets/theme/views/admin/react-bs-table";
import Api from "lib/api/httpRequestHelper";
import ToolkitTable from "components/Cards/Tables/ToolkitTable";
import DialogMonitorPedidosPI from "components/Dialogs/DialogMonitorPedidosPI";
import IconButtonLoader from "components/Buttons/IconButtonLoader";

import {
  columnPriceField,
  columnNumberField,
  columnTextField,
} from "utils/tables-utils";
import { IsMobile, IsMobile900 } from "utils/is-mobile";

const useStyles = makeStyles(componentStyles);

const exportIconStyle = {
  width: 25,
  height: 25,
  marginLeft: 15,
};

const TabelaNotasFiscais = ({
  dataTable,
  handleSearch,
  searching,
  currentSizePerPage,
  currentPage,
  totalRegistros,
}) => {
  const classes = useStyles();
  const snackbar = useSnackbar();
  const componentRef = useRef(null);

  const [loading, setLoading] = useState(false);

  const [reprocessOrder, setReprocessOrder] = useState(false);
  const [orderError, setOrderError] = useState("");
  const [openErrorDialog, setOpenErrorDialog] = useState(false);
  const [successReprocessing, setSuccessReprocessing] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState(null);

  const isMobile = IsMobile();
  const isMobile900 = IsMobile900();

  const columnModel = (
    classes,
    handleReprocessOrder,
    reprocessOrder,
    setOrderError,
    setOpenErrorDialog,
    successReprocessing,
    setSelectedOrder
  ) => [
    {
      dataField: "StatusIntegracao",
      text: "Status Integração",
      align: "center",
      isDummyField: true,
      csvExport: false,
      headerAlign: "center",
      formatter: (cell, row) =>
        row?.StatusIntegracao ? "Integrado" : "Erro Integração",
    },
    columnTextField(
      {
        dataField: "StatusPedido",
        text: "Status Pedido",
        align: "center",
      },
      false
    ),
    columnTextField(
      {
        dataField: "DataPedidoFormat",
        text: "Data do Pedido",
        align: "center",
      },
      false
    ),
    columnTextField(
      {
        dataField: "IdPedido",
        text: "Número Pedido",
        align: "center",
        formatter: (cell, row) => (
          <div style={{ cursor: "pointer", textDecoration: "underline" }}>
            <IconButtonLoader
              title="Visualizar pedido"
              className={classes.margin}
              size="small"
              onClick={() => {
                setSelectedOrder(row);
              }}
              icon={
                reprocessOrder ? (
                  <CircularProgress style={exportIconStyle} />
                ) : (
                  <ErrorIcon />
                )
              }
            />
          </div>
        ),
      },
      false
    ),
    columnTextField(
      {
        dataField: "Loja",
        text: "Loja",
      },
      false
    ),
    columnTextField(
      {
        dataField: "Cnpj",
        text: "CNPJ",
        align: "center",
        formatter: (params) => {
          return MaskAndValid.maskCPNJCPF(params);
        },
      },
      true
    ),
    columnTextField(
      {
        dataField: "NomeConsumidor",
        text: "Cliente",
      },
      true
    ),
    columnTextField(
      {
        dataField: "CpfConsumidor",
        text: "CPF",
        align: "center",
        formatter: (params) => {
          return MaskAndValid.maskCPNJCPF(params);
        },
      },
      false
    ),
    {
      dataField: "verErroGerado",
      isDummyField: true,
      text: "Erro Gerado",
      headerAlign: "center",
      align: "center",
      sort: false,
      formatter: (cell, row) =>
        !row?.StatusIntegracao ? (
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <IconButtonLoader
              title="Ver erro gerado"
              className={classes.margin}
              size="small"
              onClick={() => {
                setOrderError(row?.MensagemErro);
                setOpenErrorDialog(true);
              }}
              icon={
                reprocessOrder ? (
                  <CircularProgress style={exportIconStyle} />
                ) : (
                  <ErrorIcon />
                )
              }
            />
          </div>
        ) : null,
      csvExport: false,
    },
    {
      dataField: "reprocessar",
      isDummyField: true,
      text: "Reprocessar",
      headerAlign: "center",
      align: "center",
      sort: false,
      formatter: (cell, row) => {
        return (
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            {!row?.StatusIntegracao ? (
              <>
                {row?.StatusNotificacao ? (
                  <span>Tentativas excedidas</span>
                ) : (
                  <IconButtonLoader
                    title="Reprocessar pedido"
                    className={classes.margin}
                    size="small"
                    onClick={() => handleReprocessOrder(row)}
                    icon={
                      reprocessOrder ? (
                        <CircularProgress style={exportIconStyle} />
                      ) : (
                        <CacheIcon />
                      )
                    }
                  />
                )}
              </>
            ) : null}
          </div>
        );
      },
      csvExport: false,
    },
  ];

  const modelTableItemDetail = [
    columnTextField(
      {
        dataField: "Produto",
        text: "Produto",
        align: "left",
      },
      false
    ),
    columnNumberField(
      {
        dataField: "Quantidade",
        text: "Quantidade",
      },
      false
    ),
    columnPriceField(
      {
        dataField: "ValorProdutoField",
        text: "Valor Produto",
      },
      false
    ),
  ];

  const handleRefreshTable = () => {
    handleSearch({ page: currentPage, sizePerPage: currentSizePerPage });
  };

  const handleReprocessOrder = useCallback(
    async (row) => {
      setReprocessOrder(true);
      setSuccessReprocessing(false);

      await Api.post(
        "create-notification-order-seta",
        "seta",
        {
          reprocessar: "true",
          idPedido: row?.IdPedido,
          idPessoaLoja: row?.IdPessoaLoja?.toString(),
        },
        true
      )
        .then(() => {
          setSuccessReprocessing(true);
          snackbar.showMessage(
            "Reprocessando pedido. Aguarde alguns instantes...",
            {
              severity: "success",
            }
          );
        })
        .catch((error) => {
          snackbar.showMessage("Erro ao reprocessar pedido. Tente novamente.", {
            severity: "error",
          });
          console.log(error);        })
        .finally(() => setReprocessOrder(false));
    },
    [snackbar]
  );

  const doSearch = useCallback(async (page, sizePerPage) => {
    setLoading(true);

    await handleSearch({ page, sizePerPage });

    setLoading(false);
  }, []);

  const handlePageChange = useCallback(
    (page, sizePerPage) => {
      doSearch(page, sizePerPage);
    },
    [doSearch]
  );

  const handleSizePerPageChange = useCallback(
    (sizePerPage, page) => {
      doSearch(page, sizePerPage);
    },
    [doSearch]
  );

  const expandRow = {
    renderer: (row) => (
      <>
        {row?.ItensPedido?.length !== 0 ? (
          <Grid item xs={10}>
            <ToolkitProvider
              data={row?.ItensPedido}
              keyField={"IdProduto"}
              columns={modelTableItemDetail}
            >
              {(props) => (
                <div>
                  <BootstrapTable
                    classes={classes.table}
                    ref={componentRef}
                    {...props.baseProps}
                    bootstrap4={true}
                    bordered={false}
                    condensed={true}
                    striped={true}
                    hover={true}
                  />
                </div>
              )}
            </ToolkitProvider>
          </Grid>
        ) : (
          <>
            {row?.ItensPedido?.length === 0 && (
              <Grid
                item
                xs={6}
                style={{
                  display: "flex",
                  justifyContent: isMobile900 ? "flex-start" : "center",
                  flexDirection: isMobile ? "column" : "row",
                  marginBottom: "-1rem",
                }}
              >
                <p
                  style={{
                    color: "#DF767D",
                    fontWeight: "bold",
                    fontSize: "0.8rem",
                    marginBottom: isMobile && "-0.2rem",
                  }}
                >
                  Itens indisponíveis para consulta no momento.
                </p>
                <p
                  style={{
                    color: "#DF767D",
                    fontWeight: "bold",
                    fontSize: "0.8rem",
                  }}
                >
                  Tente novamente mais tarde.
                </p>
              </Grid>
            )}
          </>
        )}
      </>
    ),
    onlyOneExpanding: true,
    showExpandColumn: true,
    expandHeaderColumnRenderer: () => (
      <div
        style={{ cursor: "none" }}
        onClick={(e) => {
          e.stopPropagation();
        }}
      >
        {" "}
      </div>
    ),
    expandColumnRenderer: ({ expanded }) => {
      if (expanded) {
        return <ExpandLessIcon />;
      }
      return <ExpandMoreIcon />;
    },
  };

  const rowStyle = (row) => {
    const style = { backgroundColor: "#fbc6ca" };
    if (!row.StatusIntegracao) {
      return style;
    }
  };

  const handleSelectRow = (data) => {
    let url = "";

    url = `/admin/atendimentos-prateleira-infinita?idLoja=${data?.IdPessoaLoja}&numeroPedido=${data?.IdPedido}`;
    return window.open(url, "_blank");
  };

  useEffect(() => {
    if (selectedOrder) {
      handleSelectRow(selectedOrder, "Cancelado");
    }
  }, [selectedOrder]);

  useEffect(() => {
    if (successReprocessing) {
      setTimeout(() => {
        handleRefreshTable();
      }, 900000);
    }
  }, [successReprocessing]);

  return (
    <>
      <ToolkitTable
        searched={Boolean(dataTable)}
        keyField="IdPedido"
        dataTable={dataTable}
        styledRow={rowStyle}
        // onRowClick={(_, row) => handleSelectRow(row, "Cancelado")}
        columnModel={columnModel(
          classes,
          handleReprocessOrder,
          reprocessOrder,
          setOrderError,
          setOpenErrorDialog,
          successReprocessing.valueOf,
          setSelectedOrder
        )}
        pagination={{
          options: [25, 50, 100],
          defaultOption: currentSizePerPage,
          totalSize: totalRegistros,
          showTotal: true,
          page: currentPage,
          onPageChange: handlePageChange,
          sizePerPage: currentSizePerPage,
          onSizePerPageChange: handleSizePerPageChange,
        }}
        loading={loading}
        notFoundTitle="Sua pesquisa não encontrou pedidos correspondentes."
        bootstrapTableProps={{
          expandRow,
          remote: true,
          onTableChange: () => {},
        }}
        CustomHeader={() => (
          <Grid
            container
            className="pl-4"
            style={{ padding: isMobile ? "0 24px 10px" : "0 24px" }}
          >
            <Grid item xs={isMobile ? 12 : 4} style={{ marginBottom: "1rem" }}>
              <Box component={Typography} variant="h2">
                <Box component="span">Lista de Pedidos</Box>
                <IconButtonLoader
                  size="medium"
                  extraStyle={{
                    width: 24,
                    height: 24,
                    marginLeft: "0.3rem",
                  }}
                  title="Recarregar lista"
                  onClick={() => handleRefreshTable()}
                  icon={<RefreshIcon color="primary" />}
                  aria-label="refresh"
                  color="primary"
                  disabled={searching}
                />
              </Box>
            </Grid>
          </Grid>
        )}
      />

      {openErrorDialog ? (
        <DialogMonitorPedidosPI
          erro={orderError}
          handleClose={() => {
            setOrderError(null);
            setOpenErrorDialog(false);
          }}
        />
      ) : null}
    </>
  );
};

TabelaNotasFiscais.propTypes = {
  dataTable: PropTypes.array,
};

export default TabelaNotasFiscais;
